import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { readingsApi, superAdminApi, shiftLogApi, adminApi, hydrologyApi } from '../services/api';
import { useLiveData } from '../hooks/useSocket';
import { useEstimates } from '../hooks/useEstimates';
import KPICard from '../components/Dashboard/KPICard';
import AlarmPanel from '../components/Dashboard/AlarmPanel';
import HealthScorePanel from '../components/Dashboard/HealthScorePanel';
import HydroOverviewChart from '../components/Dashboard/HydroOverviewChart';
import {
  BS_MONTHS_FULL, BS_MONTHS_SHORT,
  adStrToBS, adStrToBSSafe, bsToAD, bsDaysInMonth, bsMonthRangeAD, bsYearRangeAD,
  fmtAD, todayAD, formatBSDayLabel, formatBSDateFull, formatBSDateTime, getBSYearOptions,
  getCurrentBSYear, getCurrentBSMonth, getCurrentBSDay, formatBSMonthYear,
} from '../utils/bs';
import useAuthStore from '../store/authStore';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import type { LiveReadingsMap, GenerationEstimate, PlantWithUsers, HydrologyForecast } from '../types';


type KPIStatus = 'ok' | 'warning' | 'critical' | 'default';

function kpiStatus(
  value:      number | null,
  warnLow:    number | null | undefined,
  warnHigh:   number | null | undefined,
  alarmLow:   number | null | undefined,
  alarmHigh:  number | null | undefined,
): KPIStatus {
  if (value === null || value === undefined) return 'default';
  if ((alarmHigh != null && value > alarmHigh) || (alarmLow != null && value < alarmLow)) return 'critical';
  if ((warnHigh  != null && value > warnHigh)  || (warnLow  != null && value < warnLow))  return 'warning';
  return 'ok';
}

// ─── Production Chart helpers ─────────────────────────────────────────────────

type Period     = 'daily' | 'weekly' | 'monthly' | 'yearly';
type DashPeriod = 'hourly' | Period;

const PC_DAY_ABBRS    = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PC_YEAR_OPTIONS = getBSYearOptions(10);

function pcIsoWeek(d: Date): number {
  const u = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = u.getUTCDay() || 7;
  u.setUTCDate(u.getUTCDate() + 4 - day);
  return Math.ceil(((u.getTime() - new Date(Date.UTC(u.getUTCFullYear(), 0, 1)).getTime()) / 86_400_000 + 1) / 7);
}

function pcIsoWeekYear(d: Date): number {
  const u = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = u.getUTCDay() || 7;
  u.setUTCDate(u.getUTCDate() + 4 - day);
  return u.getUTCFullYear();
}

function pcWeekMonday(year: number, wk: number): Date {
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - jan4Day + 1 + (wk - 1) * 7);
  return mon;
}

// Week label using BS day numbers
function pcWeekLabel(d: Date): string {
  if (isNaN(d.getTime())) return 'Unknown';
  const wk = pcIsoWeek(d); const wkY = pcIsoWeekYear(d);
  const mon = pcWeekMonday(wkY, wk);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const monBS = adStrToBSSafe(fmtAD(mon));
  const sunBS = adStrToBSSafe(fmtAD(sun));
  if (!monBS || !sunBS) return `W${wk}`;
  return `W${wk} (${PC_DAY_ABBRS[mon.getDay()]} ${monBS.getDate()}–${PC_DAY_ABBRS[sun.getDay()]} ${sunBS.getDate()})`;
}

// Weeks within a BS year (for drill-down)
function pcWeeksInBSYear(bsYear: number): { weekNo: number; label: string; from: string; to: string }[] {
  const { from: bsFrom, to: bsTo } = bsYearRangeAD(bsYear);
  const result: { weekNo: number; label: string; from: string; to: string }[] = [];
  const d = new Date(bsFrom + 'T00:00:00');
  while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
  while (true) {
    const start = new Date(d);
    const endFull = new Date(d); endFull.setDate(endFull.getDate() + 6);
    const fromStr = fmtAD(start);
    if (fromStr > bsTo) break;
    const toStrFull = fmtAD(endFull);
    const toStr = toStrFull > bsTo ? bsTo : toStrFull;
    const endDate = new Date(toStr + 'T00:00:00');
    const wk = pcIsoWeek(start);
    const startBS = adStrToBS(fromStr);
    const endBS   = adStrToBS(toStr);
    result.push({
      weekNo: wk,
      label: `W${wk}: ${PC_DAY_ABBRS[start.getDay()]} ${startBS.getDate()} ${BS_MONTHS_SHORT[startBS.getMonth()]} – ${PC_DAY_ABBRS[endDate.getDay()]} ${endBS.getDate()} ${BS_MONTHS_SHORT[endBS.getMonth()]}`,
      from: fromStr, to: toStr,
    });
    d.setDate(d.getDate() + 7);
  }
  return result;
}

function pcGetRange(period: Period, bsYear: number, bsMonthIdx: number, _bsDay: number) {
  if (period === 'daily')   return bsMonthRangeAD(bsYear, bsMonthIdx); // full month, one bar per day
  if (period === 'weekly')  return bsMonthRangeAD(bsYear, bsMonthIdx);
  if (period === 'monthly') return bsYearRangeAD(bsYear);
  return { from: bsYearRangeAD(bsYear - 4).from, to: bsYearRangeAD(bsYear).to };
}

function fmtMwhAxis(mwh: number): string {
  if (mwh === 0) return '0';
  if (mwh >= 1_000) return `${(mwh / 1_000).toFixed(1)} GWh`;
  return `${Math.round(mwh)} MWh`;
}
function fmtMwh(mwh: number): string {
  if (mwh >= 1_000) return `${(mwh / 1_000).toFixed(2)} GWh`;
  return `${mwh.toFixed(2)} MWh`;
}

type ProdRow = { label: string; Planned?: number | null; Actual: number | null };

function buildProdChartData(
  summaries: { date: string; total_generation_kwh: number | string }[],
  estimates: GenerationEstimate[],
  period:    Period,
  bsYear:    number,
): ProdRow[] {
  const ptMap: Record<Period, GenerationEstimate['period_type']> = {
    daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year',
  };
  const est = estimates.filter(e => e.period_type === ptMap[period]);

  if (period === 'daily') {
    const planMap: Record<string, number> = {};
    est.forEach(e => { if (e.date) planMap[e.date] = Number(e.estimated_kwh) / 1000; });
    return summaries.filter(s => s.date).map(s => ({
      label:   formatBSDayLabel(new Date(s.date + 'T00:00:00')),
      Planned: planMap[s.date] ? +(planMap[s.date]).toFixed(2) : null,
      Actual:  +(Number(s.total_generation_kwh) / 1000).toFixed(2),
    }));
  }

  if (period === 'weekly') {
    const planMap: Record<string, number> = {};
    est.forEach(e => {
      if (e.week_no != null) { const k = `${e.year}-W${String(e.week_no).padStart(2, '0')}`; planMap[k] = (planMap[k] ?? 0) + Number(e.estimated_kwh) / 1000; }
    });
    const actualMap: Record<string, { label: string; actual: number; sort: string }> = {};
    summaries.forEach(s => {
      const d = new Date(s.date + 'T00:00:00');
      if (isNaN(d.getTime())) return;
      const nd = adStrToBSSafe(s.date); if (!nd) return;
      const wk = pcIsoWeek(d);
      // Use BS year to match the key format used when saving estimates (e.year is BS year)
      const k = `${nd.getYear()}-W${String(wk).padStart(2, '0')}`;
      if (!actualMap[k]) actualMap[k] = { label: pcWeekLabel(d), actual: 0, sort: k };
      actualMap[k].actual += Number(s.total_generation_kwh) / 1000;
    });
    return Object.entries(actualMap).sort(([, a], [, b]) => a.sort > b.sort ? 1 : -1).map(([k, v]) => ({
      label: v.label, Planned: planMap[k] ? +(planMap[k]).toFixed(2) : null, Actual: +v.actual.toFixed(2),
    }));
  }

  if (period === 'monthly') {
    const planMap: Record<string, number> = {};
    est.forEach(e => { if (e.month != null) { const k = `${e.year}-${e.month}`; planMap[k] = (planMap[k] ?? 0) + Number(e.estimated_kwh) / 1000; } });
    const actualMap = new Map<number, number>(BS_MONTHS_SHORT.map((_, i) => [i, 0]));
    summaries.forEach(s => {
      const nd = adStrToBSSafe(s.date); if (!nd) return;
      if (nd.getYear() !== bsYear) return;
      const m = nd.getMonth();
      actualMap.set(m, (actualMap.get(m) ?? 0) + Number(s.total_generation_kwh) / 1000);
    });
    return [...actualMap.entries()].sort(([a], [b]) => a - b).map(([m, actual]) => {
      const k = `${bsYear}-${m + 1}`;
      return { label: BS_MONTHS_SHORT[m], Planned: planMap[k] ? +(planMap[k]).toFixed(2) : null, Actual: +actual.toFixed(2) };
    });
  }

  // yearly — group by BS year
  const planMap: Record<string, number> = {};
  est.forEach(e => { const k = String(e.year); planMap[k] = (planMap[k] ?? 0) + Number(e.estimated_kwh) / 1000; });
  const actualMap = new Map<number, { label: string; actual: number }>();
  summaries.forEach(s => {
    const nd = adStrToBSSafe(s.date); if (!nd) return;
    const y = nd.getYear();
    if (!actualMap.has(y)) actualMap.set(y, { label: String(y), actual: 0 });
    actualMap.get(y)!.actual += Number(s.total_generation_kwh) / 1000;
  });
  return [...actualMap.entries()].sort(([a], [b]) => a - b).map(([y, v]) => ({
    label: v.label, Planned: planMap[String(y)] ? +(planMap[String(y)]).toFixed(2) : null, Actual: +v.actual.toFixed(2),
  }));
}

// ─── Period selector controls (Dashboard chart) ───────────────────────────────

interface ProdControlsProps {
  period:  DashPeriod;  onPeriod:  (p: DashPeriod) => void;
  bsYear:  number;  onBSYear:  (y: number) => void;
  bsMonth: number;  onBSMonth: (m: number) => void; // 0-indexed
  bsDay:   number;  onBSDay:   (d: number) => void;
  selWeek: number | null; onWeek: (w: number | null) => void;
}

function ProdPeriodControls({ period, onPeriod, bsYear, onBSYear, bsMonth, onBSMonth, bsDay, onBSDay, selWeek, onWeek }: ProdControlsProps) {
  const weeks    = useMemo(() => period === 'monthly' ? pcWeeksInBSYear(bsYear) : [], [period, bsYear]);
  const daysInMo = useMemo(() => bsDaysInMonth(bsYear, bsMonth), [bsYear, bsMonth]);
  const periodKeys: DashPeriod[] = ['hourly', 'daily', 'weekly', 'monthly', 'yearly'];

  return (
    <div className="flex flex-wrap gap-2 items-end mt-2 mb-3">
      {/* Period buttons */}
      <div className="flex border border-hydro-border rounded-lg overflow-hidden">
        {periodKeys.map(p => (
          <button key={p} onClick={() => { onPeriod(p); onWeek(null); }}
            className={`px-3 py-1.5 text-xs font-medium transition-colors capitalize ${
              period === p ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>

      {/* Hourly → BS year / month / day */}
      {period === 'hourly' && (
        <>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Year</label>
            <select value={bsYear} onChange={e => onBSYear(Number(e.target.value))} className="input text-xs py-1.5">
              {PC_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Month</label>
            <select value={bsMonth} onChange={e => { onBSMonth(Number(e.target.value)); onBSDay(1); }} className="input text-xs py-1.5">
              {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Day</label>
            <select value={bsDay} onChange={e => onBSDay(Number(e.target.value))} className="input text-xs py-1.5">
              {Array.from({ length: daysInMo }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Daily → BS year / month (full month shown) */}
      {period === 'daily' && (
        <>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Year</label>
            <select value={bsYear} onChange={e => onBSYear(Number(e.target.value))} className="input text-xs py-1.5">
              {PC_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Month</label>
            <select value={bsMonth} onChange={e => onBSMonth(Number(e.target.value))} className="input text-xs py-1.5">
              {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Weekly → BS month + BS year */}
      {period === 'weekly' && (
        <>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Month (BS)</label>
            <select value={bsMonth} onChange={e => onBSMonth(Number(e.target.value))} className="input text-xs py-1.5">
              {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Year (BS)</label>
            <select value={bsYear} onChange={e => onBSYear(Number(e.target.value))} className="input text-xs py-1.5">
              {PC_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Monthly → BS year + week drill-down */}
      {period === 'monthly' && (
        <>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Year (BS)</label>
            <select value={bsYear} onChange={e => { onBSYear(Number(e.target.value)); onWeek(null); }} className="input text-xs py-1.5">
              {PC_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Week</label>
            <select value={selWeek ?? ''} onChange={e => onWeek(e.target.value ? Number(e.target.value) : null)} className="input text-xs py-1.5" style={{ minWidth: 240 }}>
              <option value="">— All months —</option>
              {weeks.map(w => <option key={w.weekNo} value={w.weekNo}>{w.label}</option>)}
            </select>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Production Chart: Planned vs. Actual ────────────────────────────────────

function DailyProductionChart() {
  const { user } = useAuthStore();
  const plantId = user?.plant_id ?? 1;

  const [period,  setPeriod]  = useState<DashPeriod>('daily');
  const [bsYear,  setBSYear]  = useState(getCurrentBSYear());
  const [bsMonth, setBSMonth] = useState(getCurrentBSMonth()); // 0-indexed
  const [bsDay,   setBSDay]   = useState(getCurrentBSDay());
  const [selWeek, setSelWeek] = useState<number | null>(null);

  const hourlyDate = useMemo(
    () => period === 'hourly' ? fmtAD(bsToAD(bsYear, bsMonth, bsDay)) : '',
    [period, bsYear, bsMonth, bsDay],
  );

  const { from, to } = useMemo(
    () => period === 'hourly' ? { from: '', to: '' } : pcGetRange(period, bsYear, bsMonth, bsDay),
    [period, bsYear, bsMonth, bsDay],
  );

  const estYears = useMemo(
    () => period === 'yearly' ? Array.from({ length: 5 }, (_, i) => bsYear - 4 + i) : [bsYear],
    [period, bsYear],
  );

  const { data: hourlyRows = [], isLoading: hourlyLoading } = useQuery({
    queryKey: ['dash-prod-hourly', hourlyDate],
    queryFn:  () => shiftLogApi.hourlyTotals({ date: hourlyDate }).then(r => r.data.data),
    enabled:  period === 'hourly' && !!hourlyDate,
    staleTime: 60_000,
  });

  const { data: summaries = [], isLoading: sumLoading } = useQuery({
    queryKey: ['dash-prod-sum', from, to],
    queryFn:  () => shiftLogApi.dailyTotals({ from, to }).then(r => r.data.data),
    enabled:  period !== 'hourly',
    staleTime: 5 * 60_000,
  });

  const { data: estimates = [], isLoading: estLoading } = useEstimates(plantId, estYears, { staleTime: 10 * 60_000 });

  const weeks     = useMemo(() => period === 'monthly' ? pcWeeksInBSYear(bsYear) : [], [period, bsYear]);
  const drillWeek = selWeek != null ? weeks.find(w => w.weekNo === selWeek) : null;

  const chartData = useMemo<ProdRow[]>(() => {
    if (period === 'hourly') {
      const hourPlanMap: Record<number, number> = {};
      estimates
        .filter(e => e.period_type === 'hour' && e.date === hourlyDate)
        .forEach(e => { if (e.hour != null) hourPlanMap[e.hour] = Number(e.estimated_kwh); });
      return hourlyRows.map(h => ({
        label:   String(h.hour).padStart(2, '0') + ':00',
        Planned: hourPlanMap[h.hour] != null ? +hourPlanMap[h.hour].toFixed(2) : null,
        Actual:  +Number(h.combined_kw).toFixed(2),
      }));
    }
    if (drillWeek) {
      const dayEst = estimates.filter(e => e.period_type === 'day');
      const planMap: Record<string, number> = {};
      dayEst.forEach(e => { if (e.date) planMap[e.date] = Number(e.estimated_kwh) / 1000; });
      return summaries
        .filter(s => s.date >= drillWeek.from && s.date <= drillWeek.to)
        .map(s => ({
          label:   formatBSDayLabel(new Date(s.date + 'T00:00:00')),
          Planned: planMap[s.date] ? +(planMap[s.date]).toFixed(2) : null,
          Actual:  +(Number(s.total_generation_kwh) / 1000).toFixed(2),
        }));
    }
    return buildProdChartData(summaries, estimates, period, bsYear);
  }, [period, hourlyRows, hourlyDate, summaries, estimates, bsYear, drillWeek]);

  const hasPlanned = chartData.some(r => r.Planned != null && r.Planned > 0);
  const isLoading  = period === 'hourly' ? (hourlyLoading || estLoading) : (sumLoading || estLoading);
  const unit       = period === 'hourly' ? 'kW' : 'MWh';

  const yTickFmt = unit === 'MWh' ? fmtMwhAxis : (v: number) => `${v} kW`;
  const yTipFmt  = unit === 'MWh' ? (v: number) => [fmtMwh(v)] : (v: number) => [`${v.toFixed(1)} kW`];

  const chartTitle = period === 'hourly'
    ? `Hourly Production — ${hourlyDate}`
    : drillWeek
      ? `Production — ${drillWeek.label}`
      : 'Production: Planned vs. Actual';

  return (
    <div className="card">
      <p className="text-sm font-semibold text-slate-900">{chartTitle}</p>
      <ProdPeriodControls
        period={period} onPeriod={setPeriod}
        bsYear={bsYear} onBSYear={setBSYear}
        bsMonth={bsMonth} onBSMonth={setBSMonth}
        bsDay={bsDay} onBSDay={setBSDay}
        selWeek={selWeek} onWeek={setSelWeek}
      />
      <div className="flex gap-4 mb-3 text-xs text-slate-500">
        {hasPlanned && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-slate-300" /> Planned
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-blue-600" /> Actual
        </span>
      </div>
      {isLoading ? (
        <div className="h-64 flex items-center justify-center text-slate-400 text-sm">Loading…</div>
      ) : chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No data for selected period</div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 9 }} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false}
              tickFormatter={yTickFmt} width={72} />
            <Tooltip
              contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11 }}
              formatter={(v: number) => yTipFmt(v)}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
            {hasPlanned && <Bar dataKey="Planned" fill="#cbd5e1" radius={[2, 2, 0, 0]} />}
            <Bar dataKey="Actual" fill="#2563eb" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── 7-Day Hydrological Overview ─────────────────────────────────────────────

function HydroOverviewSection() {
  const { data: forecast, isLoading } = useQuery<HydrologyForecast>({
    queryKey: ['hydrology-forecast'],
    queryFn: () => hydrologyApi.forecast().then(r => r.data.data),
    staleTime: 3 * 60 * 60 * 1000,
    retry: 2,
  });

  if (isLoading || !forecast) return null;
  return <HydroOverviewChart days={forecast.days} />;
}

// ─── Today's Overview ─────────────────────────────────────────────────────────

function TodayOverview() {
  const today = todayAD();

  const { data, isLoading } = useQuery({
    queryKey: ['shift-log-today'],
    queryFn:  () => shiftLogApi.get(today).then(r => r.data),
    refetchInterval: 60_000,
  });

  type OverviewRow = { id: number; name: string; type: 'Generator' | 'Transformer'; hours: number; totalOutput: number; totalKwh: number };

  const rows = useMemo<OverviewRow[]>(() => {
    const map = new Map<string, OverviewRow>();
    for (const log of data?.logs ?? []) {
      const key = `${log.equipment_type}-${log.equipment_id}`;
      if (!map.has(key)) {
        map.set(key, {
          id: log.equipment_id,
          name: log.equipment_name ?? `${log.equipment_type} ${log.equipment_id}`,
          type: log.equipment_type === 'generator' ? 'Generator' : 'Transformer',
          hours: 0, totalOutput: 0, totalKwh: 0,
        });
      }
      const row = map.get(key)!;
      const output = log.equipment_type === 'generator' ? log.output_kw : log.output_mw;
      if (output != null) { row.hours++; row.totalOutput += Number(output); }
      if (log.energy_kwh != null) row.totalKwh += Number(log.energy_kwh);
    }
    return [...map.values()].sort((a, b) =>
      a.type === b.type ? a.id - b.id : a.type === 'Generator' ? -1 : 1
    );
  }, [data]);

  const totalKwh = rows.reduce((s, r) => s + r.totalKwh, 0);

  if (isLoading) return <div className="card text-center py-8 text-slate-400 text-sm">Loading…</div>;
  if (!rows.some(r => r.hours > 0)) return null;

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-hydro-border">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Today's Overview — {formatBSDateFull(new Date(today + 'T00:00:00'))}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider bg-slate-50">
              {['Equipment', 'Type', 'Hrs Recorded', 'Total Output', 'Total Energy (kWh)'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={`${row.type}-${row.id}`}
                className="table-row text-xs border-b border-hydro-border last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{row.name}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    row.type === 'Generator' ? 'bg-brand-100 text-brand-700' : 'bg-amber-100 text-amber-700'
                  }`}>{row.type}</span>
                </td>
                <td className="px-4 py-3 font-mono text-slate-700">{row.hours}</td>
                <td className="px-4 py-3 font-mono text-blue-600">
                  {row.totalOutput > 0 ? `${row.totalOutput.toFixed(2)} ${row.type === 'Generator' ? 'KW' : 'MVA'}` : '—'}
                </td>
                <td className="px-4 py-3 font-mono text-emerald-700">
                  {row.totalKwh > 0 ? row.totalKwh.toFixed(2) : '—'}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-hydro-border bg-slate-50 text-xs font-bold">
              <td colSpan={4} className="px-4 py-3 text-slate-700 uppercase tracking-wide">Total Energy</td>
              <td className="px-4 py-3 font-mono text-emerald-700">{totalKwh.toFixed(2)} kWh</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Super Admin hydropower overview ─────────────────────────────────────────

function SuperAdminDashboard() {
  const { data, isLoading } = useQuery<{ plants: PlantWithUsers[] }>({
    queryKey: ['sa-plants'],
    queryFn: () => superAdminApi.listPlants().then(r => r.data),
    refetchInterval: 60_000,
  });

  const plants = data?.plants ?? [];
  const active  = plants.filter(p => p.is_active);
  const scada   = plants.filter(p => p.scada_enabled);
  const manual  = plants.filter(p => !p.scada_enabled);
  const totalKw = plants.reduce((s, p) => s + (Number(p.capacity_kw) || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Hydropower Overview</h1>
        <p className="text-sm text-slate-500 mt-0.5">All registered hydropower plants across the platform</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card flex flex-col gap-1 py-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Total Plants</p>
          <p className="text-3xl font-bold text-slate-900 font-mono">{plants.length}</p>
          <p className="text-xs text-slate-500">{active.length} active</p>
        </div>
        <div className="card flex flex-col gap-1 py-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Total Capacity</p>
          <p className="text-3xl font-bold text-brand-600 font-mono">{totalKw > 0 ? (totalKw / 1000).toFixed(2) : '—'}</p>
          <p className="text-xs text-slate-500">MW installed</p>
        </div>
        <div className="card flex flex-col gap-1 py-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">SCADA Plants</p>
          <p className="text-3xl font-bold text-emerald-600 font-mono">{scada.length}</p>
          <p className="text-xs text-slate-500">Automated data collection</p>
        </div>
        <div className="card flex flex-col gap-1 py-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Manual Entry</p>
          <p className="text-3xl font-bold text-amber-600 font-mono">{manual.length}</p>
          <p className="text-xs text-slate-500">Operator-entered data</p>
        </div>
      </div>

      {isLoading ? (
        <div className="card text-center py-12 text-slate-400">Loading hydropowers…</div>
      ) : plants.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-slate-500 font-medium">No hydropower plants registered yet</p>
          <p className="text-xs text-slate-400 mt-1">Go to Platform → Plants to add your first hydropower plant</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plants.map(plant => (
            <PlantCard key={plant.id} plant={plant} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlantCard({ plant }: { plant: PlantWithUsers }) {
  const capacityMW = plant.capacity_kw ? (Number(plant.capacity_kw) / 1000).toFixed(2) : null;

  return (
    <div className={`card space-y-4 ${!plant.is_active ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0 ${
            plant.scada_enabled ? 'bg-brand-100' : 'bg-amber-100'
          }`}>
            💧
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 truncate">{plant.name}</p>
            <p className="text-xs text-slate-400 truncate">{plant.location ?? 'Location not set'}</p>
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
          plant.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
        }`}>
          {plant.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <p className="text-lg font-bold font-mono text-slate-900">{capacityMW ?? '—'}</p>
          <p className="text-xs text-slate-400">MW capacity</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold font-mono text-slate-900">{plant.user_count}</p>
          <p className="text-xs text-slate-400">Users</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold font-mono text-slate-900">{plant.generators?.length ?? '—'}</p>
          <p className="text-xs text-slate-400">Generators</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-hydro-border">
        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
          plant.scada_enabled
            ? 'bg-brand-100 text-brand-700'
            : 'bg-amber-100 text-amber-700'
        }`}>
          {plant.scada_enabled ? '⚡ SCADA' : '✍️ Manual Entry'}
        </span>
        {plant.commissioned_at && (
          <p className="text-xs text-slate-400">
            Since {formatBSMonthYear(new Date(plant.commissioned_at))}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard (role-aware) ──────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuthStore();
  if (user?.role === 'super_admin') return <SuperAdminDashboard />;
  return <PlantDashboard />;
}

// ─── SCADA live monitoring dashboard ─────────────────────────────────────────

function ScadaDashboard({ plantName }: { plantName: string }) {
  const [liveMap, setLiveMap] = useState<LiveReadingsMap>({});

  const { data: initialLive } = useQuery<LiveReadingsMap>({
    queryKey: ['live-readings'],
    queryFn: () => readingsApi.live().then((r) => r.data.data),
    refetchInterval: 30_000,
  });

  const handleLiveData = useCallback(({ data }: { data: LiveReadingsMap }) => {
    setLiveMap((prev) => ({ ...prev, ...data }));
  }, []);

  useLiveData(handleLiveData);

  const live: LiveReadingsMap = { ...(initialLive ?? {}), ...liveMap };

  const get = (tagName: string): number | null => live[tagName]?.value ?? null;

  const power   = get('Generator.Power');
  const freq    = get('Generator.Frequency');
  const voltRY  = get('Generator.Voltage_RY');
  const voltYB  = get('Generator.Voltage_YB');
  const voltBR  = get('Generator.Voltage_BR');
  const currR   = get('Generator.Current_R');
  const currY   = get('Generator.Current_Y');
  const currB   = get('Generator.Current_B');
  const pf      = get('Generator.PowerFactor');
  const kvar    = get('Generator.kVAR');
  const energy  = get('Plant.TotalEnergy');
  const wlevel  = get('Plant.WaterLevel');
  const turbRPM = get('Turbine.Speed');
  const genTemp = get('Generator.Temperature');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Plant Dashboard</h1>
          <p className="text-sm text-slate-500">{plantName} — Live Monitoring</p>
        </div>
        <span className="text-xs text-slate-400">{formatBSDateTime(new Date())}</span>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
        <KPICard label="Active Power"    value={power?.toFixed(1)}    unit="kW"   icon="⚡" status={kpiStatus(power, 500, 5000, 100, 5500)} />
        <KPICard label="Frequency"       value={freq?.toFixed(3)}     unit="Hz"   icon="〰️" status={kpiStatus(freq, 49.5, 50.5, 49.0, 51.0)} />
        <KPICard label="Voltage R-Y"     value={voltRY?.toFixed(1)}   unit="V"    icon="🔋" status={kpiStatus(voltRY, 395, 425, 380, 440)} />
        <KPICard label="Power Factor"    value={pf?.toFixed(3)}       unit=""     icon="📐" status={kpiStatus(pf, 0.8, null, 0.7, null)} />
        <KPICard label="Water Level"     value={wlevel?.toFixed(2)}   unit="m"    icon="💧" status={kpiStatus(wlevel, 6.0, null, 5.0, null)} />
        <KPICard label="Turbine Speed"   value={turbRPM?.toFixed(0)}  unit="RPM"  icon="🔄" status={kpiStatus(turbRPM, 250, 550, 200, 600)} />
        <KPICard label="Gen Temperature" value={genTemp?.toFixed(1)}  unit="°C"   icon="🌡️" status={kpiStatus(genTemp, null, 80, null, 90)} />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard label="Voltage Y-B"  value={voltYB?.toFixed(1)} unit="V"    status={kpiStatus(voltYB, 395, 425, 380, 440)} />
        <KPICard label="Voltage B-R"  value={voltBR?.toFixed(1)} unit="V"    status={kpiStatus(voltBR, 395, 425, 380, 440)} />
        <KPICard label="Current R"    value={currR?.toFixed(1)}  unit="A"    status={kpiStatus(currR, 50, 700, 0, 750)} />
        <KPICard label="Current Y"    value={currY?.toFixed(1)}  unit="A"    status={kpiStatus(currY, 50, 700, 0, 750)} />
        <KPICard label="Current B"    value={currB?.toFixed(1)}  unit="A"    status={kpiStatus(currB, 50, 700, 0, 750)} />
        <KPICard label="kVAR"         value={kvar?.toFixed(1)}   unit="kVAR" />
      </div>

      {/* Energy meter + Alarm Panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card col-span-1 flex flex-col justify-center items-center py-6">
          <p className="kpi-label">Total Energy (Meter)</p>
          <p className="text-4xl font-bold font-mono text-brand-600 mt-2">
            {energy != null ? Number(energy).toLocaleString('en-IN') : '—'}
          </p>
          <p className="text-sm text-slate-500 mt-1">kWh</p>
        </div>
        <div className="card col-span-2">
          <AlarmPanel compact />
        </div>
      </div>

      <HealthScorePanel />
      <DailyProductionChart />
      <HydroOverviewSection />
      <TodayOverview />
    </div>
  );
}

// ─── Manual (shift-log) dashboard ────────────────────────────────────────────

function ManualDashboard({ plantName }: { plantName: string }) {
  const today = todayAD();

  const { data: logData, isLoading } = useQuery({
    queryKey: ['shift-log-today'],
    queryFn:  () => shiftLogApi.get(today).then(r => r.data),
    refetchInterval: 60_000,
  });

  // Latest hour per generator (highest hour value recorded today)
  const latestByGen = useMemo(() => {
    const map = new Map<number, typeof logData extends undefined ? never : NonNullable<typeof logData>['logs'][number]>();
    for (const log of logData?.logs ?? []) {
      if (log.equipment_type !== 'generator') continue;
      const prev = map.get(log.equipment_id);
      if (!prev || log.hour > prev.hour) map.set(log.equipment_id, log);
    }
    return [...map.values()].sort((a, b) => a.equipment_id - b.equipment_id);
  }, [logData]);

  // Aggregate totals per generator for today
  type GenTotals = { id: number; name: string; energyKwh: number; hours: number; latestKw: number | null; latestHz: number | null; latestPF: number | null; latestVoltRY: number | null };
  const genTotals = useMemo<GenTotals[]>(() => {
    const map = new Map<number, GenTotals>();
    for (const log of logData?.logs ?? []) {
      if (log.equipment_type !== 'generator') continue;
      if (!map.has(log.equipment_id)) {
        map.set(log.equipment_id, {
          id: log.equipment_id,
          name: log.equipment_name ?? `Generator ${log.equipment_id}`,
          energyKwh: 0, hours: 0,
          latestKw: null, latestHz: null, latestPF: null, latestVoltRY: null,
        });
      }
      const g = map.get(log.equipment_id)!;
      if (log.energy_kwh != null) g.energyKwh += Number(log.energy_kwh);
      if (log.output_kw  != null) g.hours++;
    }
    // Fill latest-hour readings
    for (const latest of latestByGen) {
      const g = map.get(latest.equipment_id);
      if (!g) continue;
      g.latestKw     = latest.output_kw    != null ? Number(latest.output_kw)    : null;
      g.latestHz     = latest.frequency_hz != null ? Number(latest.frequency_hz) : null;
      g.latestPF     = latest.power_factor != null ? Number(latest.power_factor) : null;
      g.latestVoltRY = latest.voltage_ry_kv != null ? Number(latest.voltage_ry_kv) * 1000 : null;
    }
    return [...map.values()].sort((a, b) => a.id - b.id);
  }, [logData, latestByGen]);

  const totalEnergyKwh = genTotals.reduce((s, g) => s + g.energyKwh, 0);
  const lastHour       = latestByGen.length > 0 ? Math.max(...latestByGen.map(l => l.hour)) : null;
  const lastHourLabel  = lastHour != null ? `${String(lastHour).padStart(2,'0')}:00 – ${String(lastHour + 1).padStart(2,'0')}:00` : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Plant Dashboard</h1>
          <p className="text-sm text-slate-500">{plantName} — Shift Log</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">{formatBSDateTime(new Date())}</p>
          {lastHourLabel && (
            <p className="text-xs text-brand-600 font-medium mt-0.5">Last logged: {lastHourLabel}</p>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="card text-center py-10 text-slate-400 text-sm">Loading shift log…</div>
      ) : genTotals.length === 0 ? (
        <div className="card text-center py-10 text-slate-400">
          <p className="text-base font-medium text-slate-500">No shift log entries for today</p>
          <p className="text-sm mt-1">Entries recorded in the Shift Log page will appear here.</p>
        </div>
      ) : (
        <>
          {/* Energy summary card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card flex flex-col justify-center items-center py-6">
              <p className="kpi-label">Total Energy Today</p>
              <p className="text-4xl font-bold font-mono text-brand-600 mt-2">
                {totalEnergyKwh > 0 ? totalEnergyKwh.toLocaleString('en-IN', { maximumFractionDigits: 1 }) : '—'}
              </p>
              <p className="text-sm text-slate-500 mt-1">kWh</p>
            </div>
            <div className="card col-span-2">
              <AlarmPanel compact />
            </div>
          </div>

          {/* Per-generator KPI rows */}
          {genTotals.map(g => (
            <div key={g.id}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{g.name}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                <KPICard label="Power Output"  value={g.latestKw     != null ? g.latestKw.toFixed(1)     : undefined} unit="kW"  icon="⚡" status={kpiStatus(g.latestKw, 500, 5000, 100, 5500)} />
                <KPICard label="Frequency"     value={g.latestHz     != null ? g.latestHz.toFixed(3)     : undefined} unit="Hz"  icon="〰️" status={kpiStatus(g.latestHz, 49.5, 50.5, 49.0, 51.0)} />
                <KPICard label="Voltage R-Y"   value={g.latestVoltRY != null ? g.latestVoltRY.toFixed(1) : undefined} unit="V"   icon="🔋" status={kpiStatus(g.latestVoltRY, 395, 425, 380, 440)} />
                <KPICard label="Power Factor"  value={g.latestPF     != null ? g.latestPF.toFixed(3)     : undefined} unit=""    icon="📐" status={kpiStatus(g.latestPF, 0.8, null, 0.7, null)} />
                <KPICard label="Hours Logged"  value={String(g.hours)} unit="hrs" icon="🕐" />
                <KPICard label="Energy Today"  value={g.energyKwh > 0 ? g.energyKwh.toFixed(1) : undefined} unit="kWh" icon="🔌" />
              </div>
            </div>
          ))}
        </>
      )}

      <HealthScorePanel />
      <DailyProductionChart />
      <HydroOverviewSection />
      <TodayOverview />
    </div>
  );
}

// ─── Outer plant dashboard — routes on plant_type ─────────────────────────────

function PlantDashboard() {
  const { user } = useAuthStore();

  const { data: plantData, isLoading: plantLoading } = useQuery({
    queryKey: ['my-plant', user?.id],
    queryFn:  () => adminApi.plant().then(r => r.data),
    staleTime: 10 * 60_000,
  });

  const plant     = (plantData as { plant?: import('../types').Plant } | undefined)?.plant;
  const plantName = plant?.name ?? 'Dashboard';
  const isScada   = !plant || plant.plant_type === 'scada';

  if (plantLoading) return (
    <div className="flex items-center justify-center h-64 text-slate-400 text-sm">Loading…</div>
  );

  return isScada
    ? <ScadaDashboard plantName={plantName} />
    : <ManualDashboard plantName={plantName} />;
}
